/** Raw dial-code metadata before UI fields (flag, search index) are applied. */
export type PhoneCountryDef = {
  code: string;
  name: string;
  dialCode: string;
  nationalLength: number;
  minLength: number;
  /** Extra tokens for dropdown search (e.g. UAE, UK). */
  aliases?: string[];
};

const d = (
  code: string,
  name: string,
  dialCode: string,
  nationalLength: number,
  minLength = nationalLength,
  aliases?: string[],
): PhoneCountryDef => ({ code, name, dialCode, nationalLength, minLength, aliases });

/** Middle East & Gulf */
const MIDDLE_EAST: PhoneCountryDef[] = [
  d('AE', 'United Arab Emirates', '971', 9, 9, ['uae', 'emirates', 'dubai', 'abu dhabi']),
  d('SA', 'Saudi Arabia', '966', 9, 9, ['ksa', 'riyadh']),
  d('QA', 'Qatar', '974', 8, 8),
  d('KW', 'Kuwait', '965', 8, 8),
  d('BH', 'Bahrain', '973', 8, 8),
  d('OM', 'Oman', '968', 8, 8),
  d('JO', 'Jordan', '962', 9, 9),
  d('LB', 'Lebanon', '961', 8, 8),
  d('IQ', 'Iraq', '964', 10, 10),
  d('IR', 'Iran', '98', 10, 10),
  d('IL', 'Israel', '972', 9, 9),
  d('PS', 'Palestine', '970', 9, 9),
  d('SY', 'Syria', '963', 9, 9),
  d('YE', 'Yemen', '967', 9, 9),
  d('TR', 'Turkey', '90', 10, 10, ['turkiye', 'istanbul']),
];

/** South Asia */
const SOUTH_ASIA: PhoneCountryDef[] = [
  d('IN', 'India', '91', 10, 10, ['bharat', 'indian']),
  d('PK', 'Pakistan', '92', 10, 10),
  d('BD', 'Bangladesh', '880', 10, 10),
  d('LK', 'Sri Lanka', '94', 9, 9),
  d('NP', 'Nepal', '977', 10, 10),
  d('AF', 'Afghanistan', '93', 9, 9),
  d('BT', 'Bhutan', '975', 8, 8),
  d('MV', 'Maldives', '960', 7, 7),
];

/** East & Southeast Asia */
const ASIA_PACIFIC: PhoneCountryDef[] = [
  d('CN', 'China', '86', 11, 11),
  d('JP', 'Japan', '81', 10, 10),
  d('KR', 'South Korea', '82', 10, 10, ['korea', 'republic of korea']),
  d('SG', 'Singapore', '65', 8, 8),
  d('MY', 'Malaysia', '60', 10, 9),
  d('TH', 'Thailand', '66', 9, 9),
  d('VN', 'Vietnam', '84', 10, 9),
  d('PH', 'Philippines', '63', 10, 10),
  d('ID', 'Indonesia', '62', 11, 9),
  d('HK', 'Hong Kong', '852', 8, 8),
  d('TW', 'Taiwan', '886', 9, 9),
  d('MO', 'Macau', '853', 8, 8),
  d('KH', 'Cambodia', '855', 9, 9),
  d('LA', 'Laos', '856', 10, 8),
  d('MM', 'Myanmar', '95', 10, 8),
  d('BN', 'Brunei', '673', 7, 7),
  d('MN', 'Mongolia', '976', 8, 8),
  d('TL', 'Timor-Leste', '670', 8, 8),
];

/** Russia, CIS & Eastern Europe */
const RUSSIA_CIS: PhoneCountryDef[] = [
  d('RU', 'Russia', '7', 10, 10, ['russian federation', 'moscow']),
  d('KZ', 'Kazakhstan', '7', 10, 10),
  d('UA', 'Ukraine', '380', 9, 9),
  d('BY', 'Belarus', '375', 9, 9),
  d('UZ', 'Uzbekistan', '998', 9, 9),
  d('AZ', 'Azerbaijan', '994', 9, 9),
  d('GE', 'Georgia', '995', 9, 9),
  d('AM', 'Armenia', '374', 8, 8),
  d('KG', 'Kyrgyzstan', '996', 9, 9),
  d('TJ', 'Tajikistan', '992', 9, 9),
  d('TM', 'Turkmenistan', '993', 8, 8),
  d('MD', 'Moldova', '373', 8, 8),
];

/** Europe */
const EUROPE: PhoneCountryDef[] = [
  d('GB', 'United Kingdom', '44', 10, 10, ['uk', 'britain', 'england', 'scotland', 'wales']),
  d('DE', 'Germany', '49', 11, 10),
  d('FR', 'France', '33', 9, 9),
  d('IT', 'Italy', '39', 10, 9),
  d('ES', 'Spain', '34', 9, 9),
  d('PT', 'Portugal', '351', 9, 9),
  d('NL', 'Netherlands', '31', 9, 9, ['holland']),
  d('BE', 'Belgium', '32', 9, 9),
  d('CH', 'Switzerland', '41', 9, 9),
  d('AT', 'Austria', '43', 10, 10),
  d('SE', 'Sweden', '46', 9, 9),
  d('NO', 'Norway', '47', 8, 8),
  d('DK', 'Denmark', '45', 8, 8),
  d('FI', 'Finland', '358', 10, 9),
  d('IE', 'Ireland', '353', 9, 9),
  d('PL', 'Poland', '48', 9, 9),
  d('CZ', 'Czech Republic', '420', 9, 9, ['czechia']),
  d('SK', 'Slovakia', '421', 9, 9),
  d('HU', 'Hungary', '36', 9, 9),
  d('RO', 'Romania', '40', 9, 9),
  d('BG', 'Bulgaria', '359', 9, 9),
  d('GR', 'Greece', '30', 10, 10),
  d('HR', 'Croatia', '385', 9, 9),
  d('SI', 'Slovenia', '386', 9, 9),
  d('RS', 'Serbia', '381', 9, 9),
  d('BA', 'Bosnia and Herzegovina', '387', 8, 8, ['bosnia']),
  d('ME', 'Montenegro', '382', 8, 8),
  d('MK', 'North Macedonia', '389', 8, 8, ['macedonia']),
  d('AL', 'Albania', '355', 9, 9),
  d('XK', 'Kosovo', '383', 8, 8),
  d('EE', 'Estonia', '372', 8, 8),
  d('LV', 'Latvia', '371', 8, 8),
  d('LT', 'Lithuania', '370', 8, 8),
  d('LU', 'Luxembourg', '352', 9, 9),
  d('MT', 'Malta', '356', 8, 8),
  d('CY', 'Cyprus', '357', 8, 8),
  d('IS', 'Iceland', '354', 7, 7),
  d('LI', 'Liechtenstein', '423', 7, 7),
  d('MC', 'Monaco', '377', 8, 8),
  d('SM', 'San Marino', '378', 10, 9),
  d('AD', 'Andorra', '376', 6, 6),
  d('VA', 'Vatican City', '379', 10, 9),
];

/** Americas & Oceania (commonly used defaults) */
const AMERICAS_OCEANIA: PhoneCountryDef[] = [
  d('US', 'United States', '1', 10, 10, ['usa', 'america', 'united states of america']),
  d('CA', 'Canada', '1', 10, 10),
  d('AU', 'Australia', '61', 9, 9),
  d('NZ', 'New Zealand', '64', 9, 8),
  d('BR', 'Brazil', '55', 11, 10),
  d('MX', 'Mexico', '52', 10, 10),
  d('ZA', 'South Africa', '27', 9, 9),
];

export const PHONE_COUNTRY_DEFS: PhoneCountryDef[] = [
  ...MIDDLE_EAST,
  ...SOUTH_ASIA,
  ...ASIA_PACIFIC,
  ...RUSSIA_CIS,
  ...EUROPE,
  ...AMERICAS_OCEANIA,
];
